<template>
  <VResizeDrawer
    v-model="model"
    disable-resize-watcher
    disable-route-watchers
    save-width
    snap-back
    touchless
    handle-icon="keyboard_double_arrow_right"
    :max-width="maxDrawerWidth"
    :min-width="minDrawerWidth"
    :resizable="drawerResizable"
    :permanent="modelPermanent"
    :temporary="!modelPermanent"
    :scrim="'rgba(0, 0, 0, 0.95)'"
    :width="minDrawerWidth"
    class="kp-base-navigation-drawer position-fixed"
  >
    <div
      v-click-outside="{
        handler: handleClickOutside,
        include
      }"
    >
      <slot />
    </div>
    <template #handle>
      <div class="kp-base-navigation-drawer__resize-handle" />
    </template>
    <template
      v-if="lgAndUp"
      #append
    >
      <div
        class="text-right pr-1 position-fixed
      kp-base-navigation-drawer__dock-container border-t bg-surface"
      >
        <v-btn
          v-tooltip="modelPermanent ? t('navigation.drawer.undock') : t('navigation.drawer.dock')"
          :active="modelPermanent"
          class="kp-base-navigation-drawer__dock mr-7"
          icon="dock_to_right"
          rounded
          size="40px"
          @click="modelPermanentProxy = !modelPermanentProxy"
        />
      </div>
    </template>
  </VResizeDrawer>
</template>
<script setup>
import {
  computed, defineModel, inject, onMounted, ref, watch,
} from 'vue';
import { useDisplay } from 'vuetify';
import VResizeDrawer from '@wdns/vuetify-resize-drawer';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps(
  {
    /**
     * Customize the key to store the opened "state"
     */
    openedStorageKey: {
      type: String,
      required: false,
      default: () => 'kp-navigation-drawer-opened',
    },
    /**
     * Customize the key to store the permanent "state"
     */
    permanentStorageKey: {
      type: String,
      required: false,
      default: () => 'kp-navigation-drawer-permanent',
    },
  },
);

const { t } = useI18n();
const {
  md, lgAndUp, width, mdAndUp,
} = useDisplay();

const showNotificationsDrawer = inject('showNotificationsDrawer');

// Collapse/expand state of the drawer
const model = defineModel({ type: Boolean });
/**
 * Tells if the drawer is in permanent mode
 */
const modelPermanent = defineModel('permanent', { type: Boolean, default: false });
// Proxy to store "modelPermanent" in local storage
const modelPermanentProxy = computed({
  get: () => modelPermanent.value,
  set: (value) => {
    modelPermanent.value = value;
    localStorage.setItem(props.permanentStorageKey, value);
  },
});

// Close notification drawer if opened
watch(model, (newModelValue) => {
  if (newModelValue) {
    showNotificationsDrawer.value = false;
  } else {
    // When the drawer is closed directly, we also turns the permanent mode off
    modelPermanentProxy.value = false;
  }
});
// When we leave lgAndUp, we close the drawer and leave the permanent mode to ensure a good sync
watch(lgAndUp, () => {
  if (!lgAndUp.value) {
    model.value = false;
    modelPermanentProxy.value = false;
  }
});

// Open state of the categories
const opened = ref([]);
onMounted(() => {
  // Restores the opened state from local storage
  let fromLocalStorage = localStorage.getItem(props.openedStorageKey);
  fromLocalStorage = JSON.parse(fromLocalStorage);
  if (Array.isArray(fromLocalStorage)) {
    opened.value = fromLocalStorage;
  }

  // Load the permanent state from local storage
  const permanent = localStorage.getItem(props.permanentStorageKey);
  if (permanent === 'true') {
    model.value = true;
    modelPermanent.value = true;
  }
});
// Sync the opened state with local storage
watch(opened, (newOpenedValue) => {
  localStorage.setItem(props.openedStorageKey, JSON.stringify(newOpenedValue));
});

// The minimum width of the drawer depending on the device
const minDrawerWidth = computed(() => {
  // 80% on small device, 320px on larger ones and 360px on extra large
  if (md.value) return 320;
  if (lgAndUp.value) return 360;

  return width.value * 0.8;
});
// The maximum width of the drawer depending on the device
const maxDrawerWidth = computed(() => {
  // 80% on larger devices, 50% on smaller ones
  if (mdAndUp.value) return width.value * 0.5;

  return width.value * 0.8;
});
// Resizable drawer only for larger devices
const drawerResizable = computed(() => true);
// All this should not be necessary but VResizeDrawer is buggy
/**
 * Handle click outside the drawer
 */
function handleClickOutside() {
  // Do not close if the drawer is in permanent mode
  if (modelPermanent.value) return;
  model.value = false;
}
// When the route changes, we close the drawer
const route = useRoute();
watch(route, () => {
  handleClickOutside();
});

/**
 * The elements that do not close the drawer when clicked
 */
function include() {
  // The burger button and the resize handle do not close the drawer when clicked
  return [
    document.querySelector('.kp-app-bar__burger-button'),
    document.querySelector('.v-resize-drawer--handle-container'),
    document.querySelector('.kp-base-navigation-drawer__dock'),
  ];
}
</script>

<style lang="scss">
$scrollbar-width: 0.5rem;
$scrollbar-track-color: rgb(var(--v-theme-color_c));
$scrollbar-thumb-color: rgb(var(--v-theme-color_d));
$resize-handler-color: rgb(var(--v-theme-color_h));
$resize-handler-width: 2px;

.kp-base-navigation-drawer {
  // Native scrollbar styles
  scrollbar-width: #{$scrollbar-width};
  scrollbar-color: #{$scrollbar-thumb-color} #{$scrollbar-track-color};

  // Webkit-specific scrollbar styles
  &::-webkit-scrollbar {
    width: #{$scrollbar-width};
  }

  &::-webkit-scrollbar-track {
    background-color: #{$scrollbar-track-color};
  }

  &::-webkit-scrollbar-thumb {
    background-color: #{$scrollbar-thumb-color};
    border-radius: 0;
  }

  .v-navigation-drawer__content {
    position: relative; // So that the dock button can be positioned correctly
  }

  &__dock-container {
    bottom: 0;
    width: 100%;
  }
}

// Styles for the resize handle
.v-resize-drawer--handle-container {
  height:100% !important;
  position: absolute;
  cursor: ew-resize !important;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: $resize-handler-width;
    background-color: $resize-handler-color;
    opacity: 0; // initially invisible
    transition: opacity 0.3s ease;
  }

  &:active::before,
  &:hover::before {
    opacity: 1; // visible on hover
  }
}
</style>
